import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../api/client';

/**
 * Per-café insights popup for Billing & Revenue's "Revenue by café" table.
 *
 * A direct port of primus-admin-main/src/components/StatisticsPage.jsx --
 * same color palette, same KPI-card/chart/heatmap/pie/table layout, same
 * search+sort+paginate tables, same CSV export, same "Live" auto-refresh
 * indicator -- just fetched for one café explicitly (GET
 * /internal/cafes/{id}/stats) instead of the café admin's own JWT-scoped
 * /api/analytics/*. SuperAdmin has no Tailwind CSS (StatisticsPage.jsx's
 * styling relies entirely on Tailwind utility classes), so this is
 * rewritten with plain inline styles rather than copied verbatim -- Tailwind
 * classes here would render with zero layout/color at all.
 *
 * Two deliberate differences from StatisticsPage.jsx:
 *  1. No "dummy data" fallback. The original shows a canned sample week
 *     when a café has zero real activity, labelled "Demo data". That's
 *     exactly the kind of plausible-looking-but-fake number this whole
 *     feature was built to avoid on a page a SuperAdmin makes real
 *     decisions from -- an empty café here shows a genuine empty state.
 *  2. One combined request instead of 6 parallel ones. StatisticsPage.jsx
 *     fires summary/timeseries/products/users/peak-hours/payment-breakdown
 *     separately (each with its own loading flag) because that's how
 *     /api/analytics/* is split. The SuperAdmin endpoint returns all of it
 *     in one round trip, so there's a single loading state instead of six.
 *
 * "Most Launched Game" is real data from the kiosk's own launch audit log
 * (action="game.launch.ok"), not a guess -- see
 * backend/app/api/endpoints/internal_cafe_stats.py's docstring for the
 * exact data source.
 */

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'custom', label: 'Custom Range' },
];

// Today, as a 'YYYY-MM-DD' string in the CAFÉ's time (IST) — the native max on the
// date inputs below. The backend reads a picked date as an IST calendar day, so the
// latest pickable day has to be IST's today, not the browser's: from the UK or the
// US the two differ for part of every day (en-CA formats as YYYY-MM-DD).
const todayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

// Same palette as StatisticsPage.jsx's `C` object.
const C = {
  primary: '#20B2AA',
  accent: '#9333EA',
  green: '#22c55e',
  orange: '#f97316',
  pink: '#ec4899',
  muted: 'rgba(255,255,255,0.12)',
  text: 'rgba(255,255,255,0.7)',
};
const PIE_COLORS = [C.primary, C.accent, C.green, C.orange, C.pink];
const PAYMENT_LABEL = { online: 'Online', cash: 'In-café (cash)' };

// Same fmt/fmtShort/relTime as StatisticsPage.jsx.
const fmt = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `₹${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};
const relTime = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function CafeInsightsModal({ cafeId, cafeName, onClose }) {
  const [period, setPeriod] = useState('this_month');
  // Only read when period === 'custom'. Both start empty so "Custom Range"
  // never silently fires a request for some default window — see the guard
  // in load() below.
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [liveAt, setLiveAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const isCustom = period === 'custom';
  const customReady = !isCustom || (!!customStart && !!customEnd);

  const load = useCallback(async () => {
    // Custom Range picked but one (or both) date hasn't been chosen yet —
    // nothing valid to fetch. Leave whatever's on screen alone rather than
    // erroring or silently falling back to another window.
    if (isCustom && !customReady) return;
    setLoading(true);
    setError(null);
    try {
      const params = isCustom
        ? { start_date: customStart, end_date: customEnd }
        : { period };
      const res = await api.get(`/internal/cafes/${cafeId}/stats`, { params });
      setData(res.data);
      setLiveAt(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load café insights');
    } finally {
      setLoading(false);
    }
  }, [cafeId, period, isCustom, customReady, customStart, customEnd]);

  // Clear stale data on period/range change so old numbers never bleed
  // through, same as StatisticsPage.jsx.
  useEffect(() => { setData(null); }, [period, customStart, customEnd]);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 10s while the popup is open, same as StatisticsPage.jsx.
  useEffect(() => {
    timerRef.current = setInterval(load, 10_000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const s = data?.summary || {};
  const series = data?.timeseries || [];
  const peak = data?.peak_hours || [];
  const payment = data?.payment_breakdown || [];
  // The backend now answers with the café Statistics page's own figures
  // (revenue.py): total_revenue, by_purpose, total_income = game time,
  // total_sales = shop. An older backend (a dashboard deploy lands first)
  // sends neither total_revenue nor by_purpose, so keep its old cards then.
  const moneyIn = Object.prototype.hasOwnProperty.call(s, 'total_revenue');
  const fmtOrDash = (n) => (n === null || n === undefined ? '—' : fmt(n));
  const byPurpose = (s.by_purpose || []).filter((p) => p.amount !== 0);
  // 24 hours or up to a month of days: label every 4th point on a long axis,
  // every point on a week's 7 days.
  const axisInterval = series.length > 12 ? 3 : 0;
  // A pie can't draw a negative slice; refunds are netted into each method.
  const paymentSlices = payment.filter((p) => p.total > 0);

  const exportCsv = () => {
    if (!data) return;
    const cell = (v) => {
      if (v === null || v === undefined) return '';
      const str = String(v);
      return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const periodLabel = isCustom
      ? `${customStart} to ${customEnd}`
      : (PERIODS.find((p) => p.key === period) || {}).label || period;
    const rows = [
      ['Café Statistics Export'],
      ['Café', cafeName || cafeId],
      ['Period', periodLabel],
      ['Exported at', new Date().toISOString()],
      [],
      ['Metric', 'Value'],
      ...(moneyIn
        ? [
          ['Revenue (money that came in)', s.total_revenue ?? ''],
          ...(s.by_purpose || []).map((p) => [`  ${p.label}`, p.amount]),
          ['Game time', s.total_income ?? ''],
          ['Shop', s.total_sales ?? ''],
        ]
        : [
          ['Total Sales', s.total_sales ?? 0],
          ['Total Income', s.total_income ?? 0],
        ]),
      ['Wallet Topups', s.wallet_topups ?? 0],
      ['Total Users', s.total_users ?? 0],
      ['Total PCs', s.total_pcs ?? 0],
      ['Active Sessions', s.active_sessions ?? 0],
      ['Total Sessions', s.total_sessions ?? 0],
      [],
      ['Revenue by Payment Method'],
      ['Method', 'Count', 'Total'],
      ...payment.map((p) => [PAYMENT_LABEL[p.method] || p.method, p.count, p.total]),
      [],
      ['Product', 'Price', 'Qty', 'Revenue'],
      ...(data.top_products || []).map((p) => [p.name, p.price, p.qty, p.revenue]),
      [],
      ['User', 'Sessions', 'Spend', 'Last Active'],
      ...(data.top_users || []).map((u) => [u.name, u.session_count, u.total_spend, u.last_active]),
      [],
      ['Sales Over Time'],
      [data.period?.bucket === 'day' ? 'Day' : 'Hour', 'Revenue', 'Sessions'],
      ...series.map((r) => [r.label, r.revenue, r.sessions]),
      [],
      ['Peak Hours (24h)'],
      ['Hour', 'Sessions', 'Revenue'],
      ...peak.map((r) => [r.label, r.sessions, r.revenue]),
    ];
    const csv = rows.map((r) => r.map(cell).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = todayStr(); // the café-local date, as on the picker
    a.href = url;
    a.download = `cafe_${cafeId}_stats_${period}_${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#0f1117', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 16, width: '100%', maxWidth: 1060, color: '#fff', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 18 }}>{cafeName || 'Café'} — Statistics</span>
              {liveAt && !loading && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#9CA3AF' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80', display: 'inline-block', animation: 'cafe-live-pulse 2s ease-in-out infinite' }} />
                  Live · {liveAt.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} title="Close" style={{ background: 'transparent', border: 'none', color: '#9CA3AF', cursor: 'pointer', padding: 4 }}>
            <X size={22} />
          </button>
        </div>

        {/* Period dropdown + refresh + export, same row as StatisticsPage.jsx's header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap' }}>
          <span style={pillBtnStyle}>Period:</span>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{ ...pillBtnStyle, cursor: 'pointer', paddingRight: 10, background: 'rgba(255,255,255,0.04)' }}
          >
            {PERIODS.map((p) => (
              <option key={p.key} value={p.key} style={{ background: '#0f1117', color: '#fff' }}>{p.label}</option>
            ))}
          </select>
          {isCustom && (
            <>
              <input
                type="date"
                value={customStart}
                max={customEnd || todayStr()}
                onChange={(e) => setCustomStart(e.target.value)}
                style={{ ...pillBtnStyle, background: 'rgba(255,255,255,0.04)', colorScheme: 'dark' }}
              />
              <span style={{ color: '#9CA3AF' }}>to</span>
              <input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                max={todayStr()}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={{ ...pillBtnStyle, background: 'rgba(255,255,255,0.04)', colorScheme: 'dark' }}
              />
              {!customReady && (
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>Pick both dates</span>
              )}
            </>
          )}
          <button type="button" onClick={load} style={pillBtnStyle}>↻ Refresh</button>
          <button type="button" onClick={exportCsv} disabled={!data} style={{ ...pillBtnStyle, marginLeft: 'auto', opacity: data ? 1 : 0.4, background: C.accent, borderColor: C.accent, color: '#fff', fontWeight: 600 }}>
            ↓ Export CSV
          </button>
        </div>

        <div style={{ padding: 22, maxHeight: '76vh', overflowY: 'auto' }}>
          {error ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#f87171' }}>
              <p>{error}</p>
              <button type="button" onClick={load} style={{ marginTop: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* KPI cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
                {moneyIn ? (
                  <>
                    <KpiCard label="Revenue" value={fmtOrDash(s.total_revenue)} sub="money that came in" icon="💰" color={C.primary} loading={loading && !data} />
                    <KpiCard label="Game time" value={fmtOrDash(s.total_income)} sub={`${s.total_sessions ?? 0} sessions`} icon="📈" color={C.green} loading={loading && !data} />
                  </>
                ) : (
                  <>
                    <KpiCard label="Sales" value={fmt(s.total_sales)} sub={`${s.total_sessions ?? 0} sessions`} icon="💰" color={C.primary} loading={loading && !data} />
                    <KpiCard label="Income" value={fmt(s.total_income)} sub={`₹${Number(s.wallet_topups || 0).toFixed(0)} topups`} icon="📈" color={C.green} loading={loading && !data} />
                  </>
                )}
                <KpiCard label="Users" value={s.total_users ?? 0} sub={`${s.active_sessions ?? 0} active now`} icon="👥" color={C.accent} loading={loading && !data} />
                <KpiCard label="PCs" value={s.total_pcs ?? 0} sub={`${s.active_sessions ?? 0} in session`} icon="🖥️" color={C.orange} loading={loading && !data} />
              </div>

              {/* What the Revenue card is made of — the café Statistics page's
                  own breakdown, every term listed so it visibly adds up. */}
              {moneyIn && !loading && byPurpose.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 18px', margin: '-8px 0 20px', fontSize: 12.5 }}>
                  {byPurpose.map((p) => (
                    <span key={p.key} style={{ color: '#9CA3AF' }}>
                      {p.label}{' '}
                      <span style={{ color: p.amount < 0 ? '#f87171' : '#E5E7EB', fontWeight: 600 }}>
                        {p.amount < 0 ? `- ${fmt(-p.amount)}` : fmt(p.amount)}
                      </span>
                    </span>
                  ))}
                  <span style={{ color: '#6B7280' }}>=</span>
                  <span style={{ color: '#fff', fontWeight: 700 }}>{fmtOrDash(s.total_revenue)}</span>
                </div>
              )}

              {/* Sales Over Time + Sessions charts */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <ChartCard title="Sales Over Time">
                  {loading && !data ? <Skeleton h={192} /> : (
                    <ResponsiveContainer width="100%" height={192}>
                      <AreaChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="cafeRevGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={C.primary} stopOpacity={0.35} />
                            <stop offset="95%" stopColor={C.primary} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.muted} />
                        <XAxis dataKey="label" tick={{ fill: C.text, fontSize: 10 }} interval={axisInterval} />
                        <YAxis tickFormatter={fmtShort} tick={{ fill: C.text, fontSize: 10 }} width={48} />
                        <Tooltip content={<ChartTooltip />} />
                        <Area type="monotone" dataKey="revenue" name="revenue" stroke={C.primary} strokeWidth={2} fill="url(#cafeRevGrad)" dot={false} activeDot={{ r: 4 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
                <ChartCard title="User Activity (Sessions)">
                  {loading && !data ? <Skeleton h={192} /> : (
                    <ResponsiveContainer width="100%" height={192}>
                      <BarChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.muted} />
                        <XAxis dataKey="label" tick={{ fill: C.text, fontSize: 10 }} interval={axisInterval} />
                        <YAxis tick={{ fill: C.text, fontSize: 10 }} width={32} />
                        <Tooltip content={<ChartTooltip prefix="" />} />
                        <Bar dataKey="sessions" name="sessions" fill={C.accent} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
              </div>

              {/* Peak hours heatmap (2/3) + payment pie (1/3) */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
                <ChartCard title="Peak Activity Hours (24h)">
                  {loading && !data ? <Skeleton h={80} /> : <PeakHoursHeatmap data={peak} />}
                </ChartCard>
                <ChartCard title="Revenue by Payment Method">
                  {loading && !data ? (
                    <Skeleton h={140} round />
                  ) : paymentSlices.length === 0 ? (
                    <div style={{ color: '#6B7280', fontSize: 13, textAlign: 'center', padding: '28px 0' }}>No transactions</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={140}>
                        <PieChart>
                          <Pie data={paymentSlices} dataKey="total" nameKey="method" cx="50%" cy="50%" innerRadius={36} outerRadius={62} paddingAngle={3}>
                            {paymentSlices.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v) => fmtShort(v)} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ marginTop: 6 }}>
                        {paymentSlices.map((p, i) => (
                          <div key={p.method} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#D1D5DB' }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length], display: 'inline-block' }} />
                              {PAYMENT_LABEL[p.method] || p.method}
                            </span>
                            <span style={{ color: '#9CA3AF' }}>{fmtShort(p.total)} · {p.count}×</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </ChartCard>
              </div>

              {/* Top products + user activity — search + sort + paginate, like StatisticsPage.jsx */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <SectionTitle>Top Products</SectionTitle>
                  <SortableTable
                    loading={loading && !data}
                    rows={data?.top_products || []}
                    emptyMsg="No product sales in this period"
                    columns={[
                      { key: 'name', label: 'Product' },
                      { key: 'price', label: 'Price', render: fmtOrDash },
                      { key: 'qty', label: 'Qty Sold' },
                      { key: 'revenue', label: 'Revenue', render: fmt },
                    ]}
                  />
                </div>
                <div>
                  <SectionTitle>User Activity</SectionTitle>
                  <SortableTable
                    loading={loading && !data}
                    rows={data?.top_users || []}
                    emptyMsg="No user activity in this period"
                    columns={[
                      { key: 'name', label: 'User' },
                      { key: 'session_count', label: 'Sessions' },
                      { key: 'total_spend', label: 'Spend', render: fmt },
                      { key: 'last_active', label: 'Last Active', render: relTime },
                    ]}
                  />
                </div>
              </div>

              {/* Most launched game — real counts from the kiosk's own launch
                  audit events (action="game.launch.ok"), see backend docstring. */}
              <div style={{ marginTop: 22, maxWidth: 420 }}>
                <SectionTitle>Most Launched Game</SectionTitle>
                {(data?.most_launched_games || []).length > 0 && (
                  <div style={{ marginBottom: 8, fontSize: 14 }}>
                    <span style={{ color: C.accent, fontWeight: 700 }}>{data.most_launched_games[0].name}</span>
                    <span style={{ color: '#9CA3AF' }}>
                      {' '}— {data.most_launched_games[0].launches} launch{data.most_launched_games[0].launches === 1 ? '' : 'es'} this period
                    </span>
                  </div>
                )}
                <SortableTable
                  loading={loading && !data}
                  rows={data?.most_launched_games || []}
                  emptyMsg="No game launches recorded in this period"
                  columns={[
                    { key: 'name', label: 'Game' },
                    { key: 'launches', label: 'Launches' },
                  ]}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes cafe-live-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        @keyframes cafe-skeleton-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}

const pillBtnStyle = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.15)',
  color: '#9CA3AF',
  borderRadius: 999,
  padding: '5px 12px',
  fontSize: 12.5,
  cursor: 'pointer',
};

function Skeleton({ h = 16, round = false }) {
  return <div style={{ height: h, width: '100%', borderRadius: round ? '50%' : 6, background: 'rgba(255,255,255,0.08)', animation: 'cafe-skeleton-pulse 1.6s ease-in-out infinite', maxWidth: round ? h : '100%', margin: round ? '0 auto' : 0 }} />;
}

function KpiCard({ label, value, sub, icon, color, loading }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.08, background: `radial-gradient(circle at top right, ${color}, transparent 70%)` }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
        <span style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
        <span style={{ color, fontSize: 14 }}>{icon}</span>
      </div>
      {loading ? (
        <div style={{ marginTop: 6 }}><Skeleton h={22} /></div>
      ) : (
        <div style={{ fontSize: 19, fontWeight: 700, marginTop: 4, position: 'relative' }}>{value}</div>
      )}
      {sub && !loading && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, position: 'relative' }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 12 }}>
      <SectionTitle>{title}</SectionTitle>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 8 }}>
      {children}
    </div>
  );
}

function ChartTooltip({ active, payload, label, prefix = '₹' }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#0f1117', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>
      <div style={{ color: '#9CA3AF', marginBottom: 2 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 600 }}>
          {p.name}: {prefix === '₹' && p.name !== 'sessions' ? fmtShort(p.value) : p.value}
        </div>
      ))}
    </div>
  );
}

function PeakHoursHeatmap({ data }) {
  const maxSessions = useMemo(() => Math.max(...(data || []).map((d) => d.sessions), 1), [data]);
  if (!data?.length) return <div style={{ color: '#6B7280', fontSize: 13 }}>No data</div>;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4 }}>
        {data.map((d) => {
          const intensity = d.sessions / maxSessions;
          return (
            <div
              key={d.hour}
              title={`${d.label}: ${d.sessions} sessions · ${fmtShort(d.revenue)}`}
              style={{
                background: `rgba(32,178,170,${0.08 + intensity * 0.85})`,
                borderRadius: 6, minHeight: 44, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
              }}
            >
              <div style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>{d.hour}</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 9 }}>{d.sessions}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, fontSize: 10, color: '#6B7280' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(32,178,170,0.1)' }} />Low</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(32,178,170,0.5)' }} />Medium</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: 'rgba(32,178,170,0.93)' }} />High</span>
      </div>
    </>
  );
}

function SortableTable({ columns, rows, loading, emptyMsg = 'No data' }) {
  const [sortCol, setSortCol] = useState(columns[0]?.key);
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE = 5;

  const handleSort = (key) => {
    if (sortCol === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(key); setSortDir('desc'); }
    setPage(0);
  };

  const filtered = (rows || []).filter((r) =>
    columns.some((c) => String(r[c.key] ?? '').toLowerCase().includes(search.toLowerCase()))
  );
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortCol]; const bv = b[sortCol];
    if (av == null) return 1;
    if (bv == null) return -1;
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });
  const paged = sorted.slice(page * PAGE, (page + 1) * PAGE);
  const totalPages = Math.ceil(sorted.length / PAGE);

  return (
    <div style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: 8, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search…"
          style={{ width: '100%', fontSize: 12.5, padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
        />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
              {columns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => handleSort(c.key)}
                  style={{ textAlign: 'left', padding: '8px 12px', color: '#9CA3AF', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', cursor: 'pointer', userSelect: 'none' }}
                >
                  {c.label}{sortCol === c.key && <span style={{ opacity: 0.6 }}> {sortDir === 'asc' ? '↑' : '↓'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  {columns.map((c) => <td key={c.key} style={{ padding: '8px 12px' }}><Skeleton h={14} /></td>)}
                </tr>
              ))
            ) : paged.length === 0 ? (
              <tr><td colSpan={columns.length} style={{ padding: 16, textAlign: 'center', color: '#6B7280' }}>{emptyMsg}</td></tr>
            ) : paged.map((row, i) => (
              <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {columns.map((c, j) => (
                  <td key={c.key} style={{ padding: '8px 12px', color: j === 0 ? '#fff' : '#D1D5DB' }}>
                    {c.render ? c.render(row[c.key]) : (row[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 8, borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 11, color: '#9CA3AF' }}>
          <span>{sorted.length} rows</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={{ ...pillBtnStyle, padding: '2px 9px', opacity: page === 0 ? 0.3 : 1 }}>← Prev</button>
            <span style={{ padding: '2px 6px' }}>{page + 1} / {totalPages}</span>
            <button type="button" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} style={{ ...pillBtnStyle, padding: '2px 9px', opacity: page >= totalPages - 1 ? 0.3 : 1 }}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}
